import React, { createContext, useContext, useState, useEffect } from 'react';

export interface LanguageItem {
    code: string;
    name: string;
    nativeName: string;
    flag: string;
}

export const SUPPORTED_LANGUAGES: LanguageItem[] = [
    { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
    { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
];

export const TRANSLATIONS: Record<string, Record<string, string>> = {
    en: {
        'common.location': 'Location',
        'common.language': 'Language',
        'common.selectLocation': 'Select Location',
        'common.selectLanguage': 'Select Language',
        'common.close': 'Close',
        'common.search': 'Search',
        'common.confirm': 'Confirm & Continue',
        'common.current': 'Current',
        'nav.home': 'Home',
        'nav.blog': 'Blog',
        'nav.podcast': 'Podcast',
        'nav.shop': 'Shop',
        'nav.store': 'Store',
        'nav.apps': 'Apps',
        'nav.appStore': 'App Store',
        'nav.contact': 'Contact',
        'nav.about': 'About',
        'nav.products': 'Products',
        'nav.cart': 'Cart',
        'nav.orderHistory': 'Order History',
        'nav.settings': 'Settings',
        'footer.blog': 'Blog',
        'footer.podcast': 'Podcast',
        'footer.shop': 'Shop',
        'footer.appStore': 'App Store',
        'footer.allEpisodes': 'All Episodes',
        'footer.viewAll': 'View All',
        'footer.allApps': 'All Apps',
        'footer.goToTop': 'Go to Top',
        'footer.technology': 'Technology',
        'footer.lifestyle': 'Lifestyle',
        'footer.business': 'Business',
        'footer.finance': 'Finance',
        'footer.humanResource': 'Human Resource',
        'footer.marketing': 'Marketing',
        'footer.miscellaneous': 'Miscellaneous',
        'footer.pomodoro': 'Pomodoro',
        'footer.typewriting': 'Typewriting',
        'footer.selectLocationTitle': 'Select Your Region',
        'footer.selectLanguageTitle': 'Select Website Language',
        'footer.locationDescription': 'Choose your location to update store currency, pricing, and availability.',
        'footer.languageDescription': 'Select your preferred language. Navigation, menus, and footer update instantly.',
        'footer.searchRegionPlaceholder': 'Search country or currency...',
        'store.allProducts': 'All Products',
        'store.buyNow': 'Buy Now',
        'store.addToCart': 'Add to Cart',
        'store.cart': 'Shopping Cart',
        'store.checkout': 'Checkout',
        'store.applyCoupon': 'Apply Coupon',
        'store.discount': 'Discount',
        'store.off': 'OFF',
        'store.total': 'Total',
        'store.subtotal': 'Subtotal',
        'store.noProducts': 'No products found',
        'store.searchPlaceholder': 'Search products...',
        'podcast.allEpisodes': 'All Episodes',
        'podcast.showNotes': 'Show notes',
        'podcast.transcript': 'Transcript',
        'podcast.listenNow': 'Listen Now',
        'podcast.duration': 'Duration',
        'blog.readMore': 'Read More',
        'blog.minRead': 'min read',
        'blog.searchPlaceholder': 'Search posts...',
        'blog.sharePost': 'Share Post',
        'blog.tableOfContents': 'Table of Contents',
        'blog.thoughts': 'Thoughts & Comments',
        'blog.author': 'Author',
        'blog.publishedOn': 'Published on',
    },
    hi: {
        'common.location': 'स्थान',
        'common.language': 'भाषा',
        'common.selectLocation': 'क्षेत्र चुनें',
        'common.selectLanguage': 'भाषा चुनें',
        'common.close': 'बंद करें',
        'common.search': 'खोजें',
        'common.confirm': 'पुष्टि करें और जारी रखें',
        'common.current': 'वर्तमान',
        'nav.home': 'होम',
        'nav.blog': 'ब्लॉग',
        'nav.podcast': 'पॉडकास्ट',
        'nav.shop': 'दुकान',
        'nav.store': 'दुकान',
        'nav.apps': 'ऐप्स',
        'nav.appStore': 'ऐप स्टोर',
        'nav.contact': 'संपर्क',
        'nav.about': 'परिचय',
        'nav.products': 'उत्पाद',
        'nav.cart': 'कार्ट',
        'nav.orderHistory': 'ऑर्डर इतिहास',
        'nav.settings': 'सेटिंग्स',
        'footer.blog': 'ब्लॉग',
        'footer.podcast': 'पॉडकास्ट',
        'footer.shop': 'दुकान',
        'footer.appStore': 'ऐप स्टोर',
        'footer.allEpisodes': 'सभी एपिसोड',
        'footer.viewAll': 'सभी देखें',
        'footer.allApps': 'सभी ऐप्स',
        'footer.goToTop': 'शीर्ष पर जाएं',
        'footer.technology': 'प्रौद्योगिकी',
        'footer.lifestyle': 'जीवनशैली',
        'footer.business': 'व्यापार',
        'footer.finance': 'वित्त',
        'footer.humanResource': 'मानव संसाधन',
        'footer.marketing': 'मार्केटिंग',
        'footer.miscellaneous': 'विविध',
        'footer.pomodoro': 'पोमोडोरो',
        'footer.typewriting': 'टाइपराइटर',
        'footer.selectLocationTitle': 'अपना क्षेत्र चुनें',
        'footer.selectLanguageTitle': 'वेबसाइट भाषा चुनें',
        'footer.locationDescription': 'स्टोर मुद्रा, मूल्य और उपलब्धता अपडेट करने के लिए अपना क्षेत्र चुनें।',
        'footer.languageDescription': 'अपनी पसंदीदा भाषा चुनें। मेनू, हेडर और फुटर तुरंत अपडेट हो जाएंगे।',
        'footer.searchRegionPlaceholder': 'देश या मुद्रा खोजें...',
        'store.allProducts': 'सभी उत्पाद',
        'store.buyNow': 'अभी खरीदें',
        'store.addToCart': 'कार्ट में जोड़ें',
        'store.cart': 'शॉपिंग कार्ट',
        'store.checkout': 'चेकआउट',
        'store.applyCoupon': 'कूपन लागू करें',
        'store.discount': 'छूट',
        'store.off': 'छूट',
        'store.total': 'कुल',
        'store.subtotal': 'उपकुल',
        'store.noProducts': 'कोई उत्पाद नहीं मिला',
        'store.searchPlaceholder': 'उत्पाद खोजें...',
        'podcast.allEpisodes': 'सभी एपिसोड',
        'podcast.showNotes': 'शो नोट्स',
        'podcast.transcript': 'ट्रांसक्रिप्ट',
        'podcast.listenNow': 'अभी सुनें',
        'podcast.duration': 'अवधि',
        'blog.readMore': 'और पढ़ें',
        'blog.minRead': 'मिनट पठन',
        'blog.searchPlaceholder': 'लेख खोजें...',
        'blog.sharePost': 'पोस्ट साझा करें',
        'blog.tableOfContents': 'विषय-सूची',
        'blog.thoughts': 'विचार और टिप्पणियाँ',
        'blog.author': 'लेखक',
        'blog.publishedOn': 'प्रकाशित',
    }
};

interface LanguageContextValue {
    language: string;
    setLanguage: (lang: string) => void;
    currentLanguage: LanguageItem;
    languages: LanguageItem[];
    t: (key: string, fallback?: string) => string;
    isTranslating: boolean;
    setIsTranslating: (val: boolean) => void;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export const useLanguage = (): LanguageContextValue => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
};

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
    const [language, setLanguageState] = useState<string>(() => {
        return localStorage.getItem('site_language') || 'en';
    });
    const [isTranslating, setIsTranslating] = useState<boolean>(false);

    const setLanguage = (lang: string) => {
        if (lang === language) return;
        setIsTranslating(true);
        setLanguageState(lang);
        localStorage.setItem('site_language', lang);
        try {
            document.documentElement.lang = lang;
        } catch {
            // Ignore
        }

        // Smooth transition timer for skeleton loaders
        setTimeout(() => {
            setIsTranslating(false);
        }, 850);
    };

    useEffect(() => {
        try {
            document.documentElement.lang = language;
        } catch {
            // Ignore
        }
    }, [language]);

    const currentLanguage = SUPPORTED_LANGUAGES.find(l => l.code === language) || SUPPORTED_LANGUAGES[0];

    const t = (key: string, fallback?: string): string => {
        const langDict = TRANSLATIONS[language];
        if (langDict && langDict[key]) {
            return langDict[key];
        }
        const defaultDict = TRANSLATIONS['en'];
        if (defaultDict && defaultDict[key]) {
            return defaultDict[key];
        }
        return fallback !== undefined ? fallback : key;
    };

    return (
        <LanguageContext.Provider value={{
            language,
            setLanguage,
            currentLanguage,
            languages: SUPPORTED_LANGUAGES,
            t,
            isTranslating,
            setIsTranslating,
        }}>
            {children}
        </LanguageContext.Provider>
    );
};
